import {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from '@dnd-kit/core';
import React, { useCallback } from 'react';
import { UnreachableCaseError } from 'ts-essentials';
import { IScratchPadNoteFragment } from '../../types';
import {
  IRichTextEditorRef,
  ScratchpadRichTextEditor,
} from '../ScratchpadRichTextEditor';
import { ContentSplitResult } from '../useContentSplit';
import CohortNote from './CohortNote';
import DashboardNote from './DashboardNote';
import ImageNote from './ImageNote';
import ReportNote from './ReportNote';

interface IProps {
  note: IScratchPadNoteFragment;
  currentNoteId: string | null;
  editorRefs: React.MutableRefObject<{ [key: string]: IRichTextEditorRef }>;
  handleContentChange: (content: string, noteId?: string) => void;
  handleAddContent: (type: string, split?: ContentSplitResult) => void;
  handleFocus: (noteId: string | null) => void;
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners?: DraggableSyntheticListeners;
  };
}

const Note = ({
  note,
  currentNoteId,
  editorRefs,
  handleContentChange,
  handleAddContent,
  handleFocus,
  dragHandleProps,
}: IProps) => {
  const editorRef = useCallback(
    (el: IRichTextEditorRef | null) => {
      if (el && note) {
        editorRefs.current[note.id] = el;
      }
    },
    [editorRefs, note],
  );

  const typename = note.content.__typename;

  switch (typename) {
    case 'RichTextNote':
      return (
        <ScratchpadRichTextEditor
          ref={editorRef}
          value={note.content.richText.html}
          noteId={note.id}
          onChange={handleContentChange}
          handleAddContent={handleAddContent}
          onFocus={() => handleFocus(note.id)}
          isFocused={currentNoteId === note.id}
        />
      );
    case 'ImageEmbedNote':
      return (
        <ImageNote
          noteId={note.id}
          content={note.content}
          dragHandleProps={dragHandleProps}
        />
      );
    case 'ReportNote':
      return (
        <ReportNote
          noteId={note.id}
          content={note.content}
          dragHandleProps={dragHandleProps}
        />
      );
    case 'DashboardNote':
      return (
        <DashboardNote
          noteId={note.id}
          content={note.content}
          dragHandleProps={dragHandleProps}
        />
      );
    case 'CohortNote':
      return (
        <CohortNote
          noteId={note.id}
          content={note.content}
          dragHandleProps={dragHandleProps}
        />
      );
    default:
      throw new UnreachableCaseError(typename);
  }
};

export default Note;
